import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Stethoscope, LogOut, Camera, Save, Lock, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Separator } from "@/components/ui/separator";
import { getStaticAsset } from "@/utils/offlineStorage";

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState("Taibah Online Sickbay");
  const [username, setUsername] = useState(user?.name || '');
  const [profilePicture, setProfilePicture] = useState<string | null>(user?.profilePicture || null);
  const [tempUsername, setTempUsername] = useState(username);
  const [tempProfilePicture, setTempProfilePicture] = useState<string | null>(profilePicture);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadLogo = async () => {
      const cachedLogo = await getStaticAsset('school-logo');
      if (cachedLogo) {
        setLogoUrl(cachedLogo);
      }
    };
    loadLogo();
  }, []);

  useEffect(() => {
    const fetchSchoolName = async () => {
      if (!user?.schoolId) {
        console.log("No school ID found for user");
        return;
      }
      try {
        const { data, error } = await supabase
          .from('schools')
          .select('name')
          .eq('id', user.schoolId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching school:', error);
          toast({
            title: "Error",
            description: "Failed to load school information",
            variant: "destructive"
          });
          return;
        }
        if (data) {
          setTitle(`Taibah Online Sickbay - ${data.name}`);
        }
      } catch (error) {
        console.error('Error in fetchSchoolName:', error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive"
        });
      }
    };
    fetchSchoolName();
  }, [user?.schoolId, toast]);

  const handleLogout = () => {
    logout();
  };

  const handleProfilePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const fileExt = file.name.split('.').pop();
        const filePath = `${crypto.randomUUID()}.${fileExt}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('profile-pictures')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('profile-pictures')
          .getPublicUrl(filePath);

        setTempProfilePicture(publicUrl);
      } catch (error) {
        console.error('Error uploading profile picture:', error);
        toast({
          title: "Error",
          description: "Failed to upload profile picture",
          variant: "destructive"
        });
      }
    }
  };

  const handleSaveChanges = async () => {
    try {
      if (!user) return;
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: tempUsername,
          profile_picture: tempProfilePicture
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setUsername(tempUsername);
      setProfilePicture(tempProfilePicture);
      setDialogOpen(false);
      toast({
        title: "Success",
        description: "Profile updated successfully"
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive"
      });
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) {
      setTempUsername(username);
      setTempProfilePicture(profilePicture);
    }
    setDialogOpen(open);
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmNewPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Password updated successfully"
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setIsChangingPassword(false);
      setDialogOpen(false);
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update password",
        variant: "destructive"
      });
    }
  };

  const renderNavigation = () => {
    if (user?.role === 'NURSE') {
      return (
        <>
          <button
            className={`px-4 py-4 font-medium ${
              location.pathname === '/nurse-dashboard'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => navigate('/nurse-dashboard')}
          >
            Nurse Dashboard
          </button>
          <button
            className={`px-4 py-4 font-medium ${
              location.pathname === '/nurse/students'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => navigate('/nurse/students')}
          >
            Student Management
          </button>
          <button
            className={`px-4 py-4 font-medium ${
              location.pathname === '/nurse/dispensary'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => navigate('/nurse/dispensary')}
          >
            Dispensary
          </button>
          <button
            className={`px-4 py-4 font-medium ${
              location.pathname === '/nurse/settings'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => navigate('/nurse/settings')}
          >
            Sickbay Settings
          </button>
          <button
            className={`px-4 py-4 font-medium ${
              location.pathname === '/nurse/referrals'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => navigate('/nurse/referrals')}
          >
            Referrals
          </button>
          <button
            className={`px-4 py-4 font-medium ${
              location.pathname === '/nurse/visits'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => navigate('/nurse/visits')}
          >
            Visits
          </button>
          <button
            className={`px-4 py-4 font-medium ${
              location.pathname === '/nurse/history'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => navigate('/nurse/history')}
          >
            History
          </button>
          <button
            className={`px-4 py-4 font-medium ${
              location.pathname === '/nurse/reports'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => navigate('/nurse/reports')}
          >
            Reports
          </button>
          <button
            className={`px-4 py-4 font-medium ${
              location.pathname === '/nurse/special-monitoring'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => navigate('/nurse/special-monitoring')}
          >
            Special Monitoring
          </button>
        </>
      );
    }

    if (user?.role === 'SCHOOL_HEAD') {
      const items = [
        { path: '/school-head/dashboard', label: 'Overview' },
        { path: '/school-head/reports', label: 'Reports' },
        { path: '/school-head/visits', label: 'Visits' },
        { path: '/school-head/students', label: 'Students' },
        { path: '/school-head/monitoring', label: 'Special Monitoring' },
        { path: '/school-head/referrals', label: 'Referrals' },
        { path: '/school-head/nurse-performance', label: 'Nurses Performance' },
      ];
      return (
        <>
          {items.map((it) => (
            <button
              key={it.path}
              className={`px-4 py-4 font-medium ${
                location.pathname === it.path
                  ? 'border-b-2 border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => navigate(it.path)}
            >
              {it.label}
            </button>
          ))}
        </>
      );
    }

    return (
      <>
        <button
          className={`px-4 py-4 font-medium ${
            location.pathname === '/user-management'
              ? 'border-b-2 border-primary text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => navigate('/user-management')}
        >
          User Management
        </button>
        <button
          className={`px-4 py-4 font-medium ${
            location.pathname === '/academic-years'
              ? 'border-b-2 border-primary text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => navigate('/academic-years')}
        >
          Academic Years
        </button>
        <button
          className={`px-4 py-4 font-medium ${
            location.pathname === '/schools-overview'
              ? 'border-b-2 border-primary text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => navigate('/schools-overview')}
        >
          Schools Overview
        </button>
        <button
          className={`px-4 py-4 font-medium ${
            location.pathname === '/classes-and-streams'
              ? 'border-b-2 border-primary text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => navigate('/classes-and-streams')}
        >
          Classes & Streams
        </button>
      </>
    );
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-background">
      <header className="border-b">
        <div className="container flex h-[83px] items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <img
              src={logoUrl || "/lovable-uploads/97fac532-a6d0-42b3-b0d8-fbb079de920c.png"}
              alt="Taibah International School Logo"
              className="h-[55px]"
            />
            <div className="flex items-center">
              <span className="text-2xl font-bold flex items-center gap-2">
                <Stethoscope className="h-6 w-6 text-primary" />
                {isEditing ? (
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => setIsEditing(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setIsEditing(false);
                      }
                    }}
                    className="max-w-[300px] text-2xl font-bold h-10"
                    autoFocus
                  />
                ) : (
                  <span
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer hover:text-primary transition-colors"
                  >
                    {title}
                  </span>
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
              <DialogTrigger asChild>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={profilePicture || undefined} />
                    <AvatarFallback className="bg-primary/10">
                      {username.split(' ')[0][0]}
                    </AvatarFallback>
                  </Avatar>
                  <span>{username}</span>
                </div>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {isChangingPassword ? "Change Password" : "Edit Profile"}
                  </DialogTitle>
                </DialogHeader>
                {isChangingPassword ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setIsChangingPassword(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handlePasswordChange}>
                        Update Password
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6 py-4">
                    <div className="flex flex-col items-center gap-4">
                      <div className="relative group">
                        <Avatar className="h-24 w-24">
                          <AvatarImage src={tempProfilePicture || undefined} />
                          <AvatarFallback className="bg-primary/10 text-xl">
                            {tempUsername.split(' ')[0][0]}
                          </AvatarFallback>
                        </Avatar>
                        <label
                          className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity"
                          htmlFor="profile-picture-edit"
                        >
                          <Camera className="h-6 w-6 text-white" />
                        </label>
                        <input
                          type="file"
                          id="profile-picture-edit"
                          className="hidden"
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                        />
                      </div>
                      <div className="space-y-2 w-full">
                        <Label htmlFor="username">Username</Label>
                        <Input
                          id="username"
                          value={tempUsername}
                          onChange={(e) => setTempUsername(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <Button variant="outline" onClick={() => setIsChangingPassword(true)}>
                        <Lock className="h-4 w-4 mr-2" />
                        Change Password
                      </Button>
                      <Button onClick={handleSaveChanges}>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <nav className="border-b bg-muted/40">
        <div className="container flex gap-6 px-4">
          {renderNavigation()}
        </div>
      </nav>
    </div>
  );
}
